// ./hooks/useInstructor.js

import { useContext, useCallback } from 'react';
import { GlobalContext } from '../context/provider';
import instructorAPI from '../api/instructorAPI';

const useInstructor = () => {
  const { 
      authState: {authData},
      instructorState,
      instructorDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // INSTRUCTOR PROFILE
  // ============================================================================

  const getInstructorByLoginId = useCallback(async (loginId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getInstructorByLoginId(loginId);
      instructorDispatch({ type: 'SET_INSTRUCTOR_DATA', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const createInstructor = useCallback(async (loginId, newData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.createInstructor(loginId, newData);
      instructorDispatch({ type: 'SET_INSTRUCTOR_DATA', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const updateInstructor = useCallback(async (loginId, updateData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.updateInstructor(loginId, updateData);
      instructorDispatch({ type: 'UPDATE_INSTRUCTOR_PROFILE', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getAllInstructors = useCallback(async (loginId, newData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getAllInstructors(loginId, newData);
      instructorDispatch({ type: 'LOAD_INSTRUCTORS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // COURSE ASSIGNMENT
  // ============================================================================

  const assignCourse = useCallback(async (loginId, assignmentData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.assignCourse(loginId, assignmentData);
      instructorDispatch({ type: 'ADD_ASSIGNED_COURSE', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const unassignCourse = useCallback(async (loginId, courseId, unassignedById) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.unassignCourse(loginId, courseId, unassignedById);
      instructorDispatch({ type: 'REMOVE_ASSIGNED_COURSE', payload: courseId });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getAssignedCourses = useCallback(async (loginId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getAssignedCourses(loginId);
      instructorDispatch({ type: 'SET_ASSIGNED_COURSES', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseStudents = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseStudents(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_STUDENTS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // LAB GRADING
  // ============================================================================

  const getPendingLabs = useCallback(async (loginId, courseId = null) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getPendingLabs(loginId, courseId);
      instructorDispatch({ type: 'SET_PENDING_LABS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getGradedLabs = useCallback(async (loginId, courseId = null) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getGradedLabs(loginId, courseId);
      instructorDispatch({ type: 'SET_GRADED_LABS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseLabs = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseLabs(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_LABS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const gradeLab = useCallback(async (loginId, gradeData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.gradeLab(loginId, gradeData);
      instructorDispatch({ type: 'GRADE_LAB', payload: gradeData });
      instructorDispatch({ type: 'ADD_GRADING_HISTORY', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // QUIZ GRADING
  // ============================================================================

  const getPendingQuizzes = useCallback(async (loginId, courseId = null) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getPendingQuizzes(loginId, courseId);
      instructorDispatch({ type: 'SET_PENDING_QUIZZES', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseQuizzes = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseQuizzes(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_QUIZZES', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const gradeQuiz = useCallback(async (loginId, gradeData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.gradeQuiz(loginId, gradeData);
      instructorDispatch({ type: 'GRADE_QUIZ', payload: gradeData });
      instructorDispatch({ type: 'ADD_GRADING_HISTORY', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // EXAM GRADING
  // ============================================================================

  const getPendingExams = useCallback(async (loginId, courseId = null) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getPendingExams(loginId, courseId);
      instructorDispatch({ type: 'SET_PENDING_EXAMS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseExams = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseExams(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_EXAMS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const gradeExam = useCallback(async (loginId, gradeData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.gradeExam(loginId, gradeData);
      instructorDispatch({ type: 'GRADE_EXAM', payload: gradeData });
      instructorDispatch({ type: 'ADD_GRADING_HISTORY', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // CAPSTONE GRADING
  // ============================================================================

  const getPendingCapstones = useCallback(async (loginId, courseId = null) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getPendingCapstones(loginId, courseId);
      instructorDispatch({ type: 'SET_PENDING_CAPSTONES', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseCapstones = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseCapstones(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_CAPSTONES', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const gradeCapstone = useCallback(async (loginId, gradeData) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.gradeCapstone(loginId, gradeData);
      instructorDispatch({ type: 'GRADE_CAPSTONE', payload: gradeData });
      instructorDispatch({ type: 'ADD_GRADING_HISTORY', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  // ============================================================================
  // ANALYTICS & DASHBOARD
  // ============================================================================

  const getDashboard = useCallback(async (loginId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getDashboard(loginId);
      instructorDispatch({ type: 'SET_DASHBOARD_STATS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getCourseAnalytics = useCallback(async (loginId, courseId) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getCourseAnalytics(loginId, courseId);
      instructorDispatch({ type: 'SET_COURSE_ANALYTICS', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const getGradingHistory = useCallback(async (loginId, params = {}) => {
    instructorDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await instructorAPI.getGradingHistory(loginId, params);
      instructorDispatch({ type: 'SET_GRADING_HISTORY', payload: response.data });
      return response;
    } catch (error) {
      instructorDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [instructorDispatch]);

  const generateQuestionRubric = useCallback(async (loginId, questionData) => {
    instructorDispatch({ type: 'SET_RUBRIC_LOADING', payload: true });
    
    try {
      const response = await instructorAPI.generateQuestionRubric(loginId, questionData);
      
      if (response.success) {
        instructorDispatch({ 
          type: 'SET_GENERATED_RUBRIC', 
          payload: response.data 
        });
        return response;
      } else {
        throw new Error(response.message || 'Failed to generate rubric');
      }
    } catch (error) {
      instructorDispatch({ 
        type: 'SET_RUBRIC_ERROR', 
        payload: error.message 
      });
      throw error;
    }
  }, [instructorDispatch]); 

  const generateCompleteLabRequirements = useCallback(async (labObjective, labRequirements, totalPoints) => {
    try {
      const response = await instructorAPI.generateCompleteLabRequirements(
        authData?.id,
        { labObjective, labRequirements, totalPoints }
      );
      return response;
    } catch (error) {
      console.error('Failed to generate IoT requirements:', error);
      return { success: false, error: error.message };
    }
  }, [authData?.id]);

  const clearGeneratedRubric = useCallback(() => {
    instructorDispatch({ type: 'CLEAR_GENERATED_RUBRIC' });
  }, [instructorDispatch]);


  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearInstructorData = useCallback(() => {
    instructorDispatch({ type: 'CLEAR_INSTRUCTOR_DATA' });
  }, [instructorDispatch]);

  return {
    // instructorState
    instructorData: instructorState.instructorData,
    assignedCourses: instructorState.assignedCourses,
    pendingLabs: instructorState.pendingLabs,
    pendingQuizzes: instructorState.pendingQuizzes,
    pendingExams: instructorState.pendingExams,
    pendingCapstones: instructorState.pendingCapstones,
    courseStudents: instructorState.courseStudents,
    courseLabs: instructorState.courseLabs,
    courseQuizzes: instructorState.courseQuizzes,
    courseExams: instructorState.courseExams,
    courseCapstones: instructorState.courseCapstones,
    dashboardStats: instructorState.dashboardStats,
    courseAnalytics: instructorState.courseAnalytics,
    gradingHistory: instructorState.gradingHistory,
    isLoading: instructorState.isLoading,
    error: instructorState.error,
    generatedRubric: instructorState.generatedRubric,
    isRubricGenerating: instructorState.isRubricGenerating,
    rubricError: instructorState.rubricError,

    // Methods
    getInstructorByLoginId,
    createInstructor,
    updateInstructor,
    getAllInstructors,
    assignCourse,
    unassignCourse,
    getAssignedCourses,
    getCourseStudents,
    getPendingLabs,
    getGradedLabs,
    getCourseLabs,
    gradeLab,
    getPendingQuizzes,
    getCourseQuizzes,
    gradeQuiz,
    getPendingExams,
    getCourseExams,
    gradeExam,
    getPendingCapstones,
    getCourseCapstones,
    gradeCapstone,
    getDashboard,
    getCourseAnalytics,
    getGradingHistory,
    clearInstructorData,
    generateQuestionRubric,
    generateCompleteLabRequirements,
    clearGeneratedRubric,  
  };
};

export default useInstructor;